import { makeApi } from '@zodios/core';
import { z } from 'zod';

const ScoreDecision = z
  .object({ score_decision: z.enum(['select_score', 'select_not']) })
  .passthrough();
export type ScoreDecision = z.infer<typeof ScoreDecision>;
const RequestRematch = z.object({ request_rematch: z.boolean() }).passthrough();
export type RequestRematch = z.infer<typeof RequestRematch>;
const Event = z.union([ScoreDecision, RequestRematch]);
export type Event = z.infer<typeof Event>;
const GameStatus = z.enum([
  'waiting_for_players',
  'countdown',
  'pending',
  'running',
  'finished',
]);
export type GameStatus = z.infer<typeof GameStatus>;
const GameType = z.enum(['match', 'tournament']);
export type GameType = z.infer<typeof GameType>;
const GameInfo = z
  .object({
    id: z.string(),
    preset_id: z.string(),
    access_code: z.string(),
    status: GameStatus,
    starts_at: z.string().datetime({ offset: true }).optional(),
    type: GameType,
    prize_data_html: z.string().optional(),
    prize_picture_url: z.string().optional(),
  })
  .passthrough();
export type GameInfo = z.infer<typeof GameInfo>;
const CreateGame = z.object({ preset_id: z.string() }).passthrough();
export type CreateGame = z.infer<typeof CreateGame>;
const CreateGameResponse = z
  .object({
    id: z.string(),
    preset_id: z.string(),
    type: GameType,
    access_code: z.string(),
    status: GameStatus,
    round: z.number().int(),
    starts_at: z.string().datetime({ offset: true }).optional(),
  })
  .passthrough();
export type CreateGameResponse = z.infer<typeof CreateGameResponse>;
const TournamentInfo = z
  .object({
    id: z.string(),
    preset_id: z.string(),
    access_code: z.string(),
    status: GameStatus,
    starts_at: z.string().datetime({ offset: true }),
    prize_data_html: z.string().optional(),
    prize_picture_url: z.string().optional(),
  })
  .passthrough();
export type TournamentInfo = z.infer<typeof TournamentInfo>;
const Profile = z
  .object({
    id: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
    picture_url: z.string().optional(),
    created_at: z.string().datetime({ offset: true }).optional(),
    referral_code: z.string(),
    referred_count: z.number().int(),
  })
  .passthrough();
export type Profile = z.infer<typeof Profile>;
const ProfileSet = z
  .object({
    first_name: z.string(),
    last_name: z.string(),
    picture_url: z.string().optional(),
    upload_image: z.instanceof(File).optional(),
  })
  .passthrough();
export type ProfileSet = z.infer<typeof ProfileSet>;
const Statistics = z
  .object({
    total_score: z.number().int(),
    streak: z.number().int(),
    longest_streak: z.number().int(),
    games_played: z.number().int(),
    games_won: z.number().int(),
  })
  .passthrough();
export type Statistics = z.infer<typeof Statistics>;
const PastTournament = z
  .object({
    id: z.string().uuid(),
    starts_at: z.string().datetime({ offset: true }),
    score: z.number().int(),
    did_win: z.boolean().optional(),
    position: z.number().int(),
    total_participants: z.number().int(),
  })
  .passthrough();
export type PastTournament = z.infer<typeof PastTournament>;
const TournamentLeaderboardEntry = z
  .object({
    id: z.string().uuid(),
    position: z.number().int(),
    first_name: z.string(),
    last_name: z.string(),
    picture_url: z.string().optional(),
    score: z.number().int(),
    lives: z.number().int(),
    answers_total: z.number().int(),
    answers_correct: z.number().int(),
    did_win: z.boolean().optional(),
  })
  .passthrough();
export type TournamentLeaderboardEntry = z.infer<
  typeof TournamentLeaderboardEntry
>;
const LeaderboardEntry = z
  .object({
    user_id: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    picture_url: z.string().optional(),
    position: z.number().int(),
    diff: z.number().int().optional(),
    matches_played: z.number().int(),
    matches_won: z.number().int(),
    total_score: z.number().int(),
  })
  .passthrough();
export type LeaderboardEntry = z.infer<typeof LeaderboardEntry>;
const Category = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    clip_count: z.number().int(),
    preview_image_url: z.string(),
    orientation: z.enum(['landscape', 'portrait']),
  })
  .passthrough();
export type Category = z.infer<typeof Category>;

export const schemas = {
  ScoreDecision,
  RequestRematch,
  Event,
  GameStatus,
  GameType,
  GameInfo,
  CreateGame,
  CreateGameResponse,
  TournamentInfo,
  Profile,
  ProfileSet,
  Statistics,
  PastTournament,
  TournamentLeaderboardEntry,
  LeaderboardEntry,
  Category,
};

export const endpoints = makeApi([
  {
    method: 'get',
    path: '/categories',
    alias: 'getCategories',
    requestFormat: 'json',
    response: z.array(Category),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/games',
    alias: 'getGameByAccessCode',
    description: `Get info for game with provided access code`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'access_code',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: GameInfo,
    errors: [
      {
        status: 404,
        description: `No game with provided access code found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/games',
    alias: 'createGame',
    description: `Creates a game in the database. Client will still need to join the game through &#x60;/games/join&#x60; method.
Is used for the &quot;code invitation&quot; flow - client creates a game, and automatically joins it through &#x60;/games/join&#x60; method.
Same for the second opponent.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ preset_id: z.string() }).passthrough(),
      },
    ],
    response: CreateGameResponse,
    errors: [
      {
        status: 400,
        description: `Provide a start time for tournament`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/games/events',
    alias: 'sendGameEvent',
    description: `Send client event for a user action. Event is sent to current
user&#x27;s game. If user has not active game, nothing happens.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: Event,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/games/join',
    alias: 'joinGame',
    description: `Joins a random game. The server will be sending events via sse.
`,
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'get',
    path: '/healthcheck',
    alias: 'getHealthcheck',
    description: `Returns 200 if server is running normally.`,
    requestFormat: 'json',
    response: z.object({ message: z.string() }).passthrough(),
  },
  {
    method: 'get',
    path: '/leaderboard',
    alias: 'getLeaderboard',
    description: `Return leaderboard for specified date range.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().optional(),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.number().int().optional(),
      },
      {
        name: 'time_period',
        type: 'Query',
        schema: z.enum(['week', 'month', 'year']),
      },
    ],
    response: z
      .object({
        leaderboard: z.array(LeaderboardEntry),
        user_stats: LeaderboardEntry,
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/profiles',
    alias: 'getProfile',
    description: `Get profile for currently authenticated user`,
    requestFormat: 'json',
    response: Profile,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/profiles',
    alias: 'setProfile',
    description: `Update fields first name, last name, and optionally upload a profile picture.`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ProfileSet,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 413,
        description: `Image too large`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Validation error - both picture_url and upload_image were supplied`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/statistics',
    alias: 'getStatistics',
    description: `Return current total score, streak, longest streak and games played/won for the user.`,
    requestFormat: 'json',
    response: Statistics,
  },
  {
    method: 'get',
    path: '/statistics/games/:game_id',
    alias: 'getGameLeaderboard',
    description: `Returns the full leaderboard of a game, specified by its id.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'game_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        leaderboard: z.array(TournamentLeaderboardEntry),
        user_id: z.string().uuid(),
        starts_at: z.string().datetime({ offset: true }).optional(),
        game_type: GameType,
      })
      .passthrough(),
    errors: [
      {
        status: 404,
        description: `No tournament with provided id found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/statistics/tournaments',
    alias: 'listPastTournaments',
    description: `Returns paginated list of past tournaments that the user has participated in.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().optional(),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.number().int().optional(),
      },
    ],
    response: z.array(PastTournament),
  },
  {
    method: 'get',
    path: '/tournaments',
    alias: 'getNextTournamentInfo',
    description: `Get the starting time and access code of the next tournament`,
    requestFormat: 'json',
    response: TournamentInfo,
    errors: [
      {
        status: 404,
        description: `All tournaments in the past, create a future tournament`,
        schema: z.void(),
      },
    ],
  },
]);
